import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Bell, CalendarDays, CheckCircle2, CreditCard, FileText, UserRoundCheck } from 'lucide-react';
import { fetchNotifications } from '@/lib/api';
import type { Notification, NotificationPriority } from '@/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

const PRIORITY_RANK: Record<NotificationPriority, number> = {
  low: 0,
  normal: 1,
  high: 2,
  urgent: 3,
};

function getEntityPath(notification: Notification): string {
  const entityType = notification.entityType;
  const entityId = notification.entityId;

  if (!entityType || !entityId) return '/notifications';

  switch (entityType) {
    case 'candidate':
      return `/candidates/${entityId}`;
    case 'job':
      return `/jobs/${entityId}`;
    case 'company':
      return `/companies/${entityId}`;
    case 'invoice':
      return `/invoices/${entityId}`;
    case 'placement':
      return `/placements/${entityId}`;
    case 'interview':
      return '/interviews';
    case 'submission':
      return '/submissions';
    case 'application':
      return '/pipeline';
    default:
      return '/notifications';
  }
}

function getIcon(notification: Notification) {
  switch (notification.category || notification.type) {
    case 'pipeline':
      return <UserRoundCheck className="h-4 w-4 text-cyan-400" />;
    case 'interview':
      return <CalendarDays className="h-4 w-4 text-orange-400" />;
    case 'invoice':
      return <CreditCard className="h-4 w-4 text-amber-400" />;
    case 'submission':
      return <FileText className="h-4 w-4 text-purple-400" />;
    case 'mention':
      return <Bell className="h-4 w-4 text-emerald-400" />;
    default:
      return <AlertCircle className="h-4 w-4 text-text-tertiary" />;
  }
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function priorityVariant(priority: NotificationPriority): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (priority === 'urgent') return 'destructive';
  if (priority === 'high') return 'default';
  return 'secondary';
}

export function NeedsAttentionNotifications() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['notifications', 'dashboard-attention'],
    queryFn: () => fetchNotifications({ page: 1, limit: 10, unreadOnly: true }),
    refetchInterval: 30000,
  });

  const actionable = (data?.data ?? [])
    .filter((notification) => PRIORITY_RANK[notification.priority ?? 'normal'] >= PRIORITY_RANK.normal)
    .sort((a, b) => PRIORITY_RANK[b.priority ?? 'normal'] - PRIORITY_RANK[a.priority ?? 'normal'])
    .slice(0, 5);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bell className="h-5 w-5" />
              Needs your attention
            </CardTitle>
            <CardDescription>Unread high-signal notifications and follow-ups.</CardDescription>
          </div>
          {actionable.length > 0 && <Badge>{actionable.length}</Badge>}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-14 w-full" />
            ))}
          </div>
        ) : isError ? (
          <p className="text-sm text-text-tertiary">Could not load notifications. The rest of the dashboard is still available.</p>
        ) : actionable.length === 0 ? (
          <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-4">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            <div>
              <p className="font-medium text-text-primary">You're all caught up.</p>
              <p className="text-sm text-text-tertiary">No unread high-priority notifications right now.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {actionable.map((notification) => (
              <Link
                key={notification.id}
                to={getEntityPath(notification)}
                className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-3 hover:bg-white/[0.05] transition-colors"
              >
                <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-white/5">
                  {getIcon(notification)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-text-primary line-clamp-1">{notification.title}</p>
                    <Badge variant={priorityVariant(notification.priority ?? 'normal')} className="capitalize">
                      {notification.priority ?? 'normal'}
                    </Badge>
                  </div>
                  <p className="text-sm text-text-tertiary line-clamp-2 mt-0.5">{notification.message}</p>
                  <p className="text-xs text-text-tertiary mt-1">{formatRelativeTime(notification.createdAt)}</p>
                </div>
              </Link>
            ))}
            <Link to="/notifications" className="block pt-2 text-sm text-emerald-500 hover:text-emerald-400">
              View all notifications
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
